import { useState, useRef, useEffect } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Link, router } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { Layout } from "@/components/layouts/Layout"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { WorkspaceForm } from "./Form"

export default function WorkspacesNew() {
  const [scrolled, setScrolled] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const scrollContainerRef = useRef(null)
  const nameRef = useRef(null)

  useEffect(() => {
    const root = scrollContainerRef.current
    const target = nameRef.current
    if (!root || !target) return

    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  function handleScrollToTop() {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  function handleScroll(e) {
    setHasScrolled(e.currentTarget.scrollTop > 0)
  }

  function handleSubmit(data) {
    router.post("/workspaces", { workspace: data })
  }

  return (
    <Layout insetClassName="overflow-hidden">

      <header className={`flex items-center justify-between gap-4 px-4 h-16 shrink-0 transition-[width,height,border-color] ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b ${hasScrolled ? "border-border" : "border-transparent"}`}>

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" asChild className="group/back -ml-1 h-7 w-7">
                <Link href="/workspaces">
                  <ArrowLeft className="h-4 w-4 text-muted-foreground group-hover/back:text-foreground" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>All workspaces</TooltipContent>
          </Tooltip>
          <AnimatePresence>
            {scrolled && (
              <motion.button
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleScrollToTop}
                className="flex items-center gap-1.5 min-w-0 rounded-md px-2 h-7 hover:bg-accent transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium truncate">New workspace</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

      </header>

      <ScrollArea className="flex-1 min-h-0" viewportRef={scrollContainerRef} onScroll={handleScroll}>
        <div className="flex flex-col gap-8 p-6">

          <div ref={nameRef}>
            <h1 className="text-3xl font-medium">New workspace</h1>
          </div>

          <WorkspaceForm
            onSubmit={handleSubmit}
            submitLabel="Create workspace"
          />

        </div>
      </ScrollArea>

    </Layout>
  )
}
